import React, { useState, useEffect, useCallback, useRef } from "react";

import { getMe, updateProfile, changePassword, uploadAvatar } from "../../../api";
import { validatePassword, PasswordRules } from "../../../utils/passwordRules";
import PasswordInput from "../../../components/PasswordInput";
import { useNotify } from "../../../ToastContext";
import xmarkIcon from "../../../assets/xmark.svg";

const PROFILE_FIELDS = ["first_name", "last_name", "email", "location", "phone", "department", "avatar"];

function pickProfile(user) {
  const p = {};
  for (const k of PROFILE_FIELDS) p[k] = user[k] || "";
  return p;
}

const AccountSettingsPage = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const notify = useNotify();
  const [submitting, setSubmitting] = useState(false);
  const [profile, setProfile] = useState({});
  const [savedProfile, setSavedProfile] = useState({});
  const [profileSaving, setProfileSaving] = useState(false);
  const fileInputRef = useRef(null);

  const updateField = (key, value) => {
    setProfile((prev) => ({ ...prev, [key]: value }));
  };

  const profileDirty = JSON.stringify(profile) !== JSON.stringify(savedProfile);

  const saveProfile = async () => {
    setProfileSaving(true);
    try {
      let profileToSave = { ...profile };
      // If avatar is still base64, upload it first
      if (profileToSave.avatar && profileToSave.avatar.startsWith("data:")) {
        try {
          const res = await uploadAvatar(profileToSave.avatar, user?.user_id, "");
          if (res?.url) profileToSave.avatar = res.url;
        } catch {}
      }
      await updateProfile(profileToSave);
      setProfile(profileToSave);
      setSavedProfile(profileToSave);
      notify({ type: "success", title: "Saved", message: "Profile updated." });
      window.dispatchEvent(new Event("gw-profile-updated"));
    } catch (err) {
      notify({ type: "error", title: "Error", message: err.message || "Failed to save profile" });
    } finally {
      setProfileSaving(false);
    }
  };

  const handleAvatarUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return;

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = async () => {
      URL.revokeObjectURL(objectUrl);
      const max = 256;
      let w = img.width, h = img.height;
      if (w > max || h > max) {
        const scale = max / Math.max(w, h);
        w = Math.round(w * scale);
        h = Math.round(h * scale);
      }
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.85);

      try {
        const res = await uploadAvatar(dataUrl, user?.user_id, profile.avatar);
        if (res?.url) {
          updateField("avatar", res.url);
        }
      } catch (err) {
        notify({ type: "error", title: "Upload Failed", message: err.message || "Failed to upload avatar" });
      }
    };
    img.src = objectUrl;
  };

  const removeAvatar = (e) => {
    e.stopPropagation();
    updateField("avatar", "");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const loadUser = useCallback(async () => {
    try {
      const data = await getMe();
      setUser(data);
      const p = pickProfile(data);
      setProfile(p);
      setSavedProfile(p);
    } catch (err) {
      notify({ type: "error", title: "Error", message: err.message || "Failed to load user info" });
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // Refresh when the AI updates this user's profile.
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.resource === "my_profile") loadUser();
    };
    window.addEventListener("gw-ai-resource-changed", handler);
    return () => window.removeEventListener("gw-ai-resource-changed", handler);
  }, [loadUser]);

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      notify({ type: "error", title: "Error", message: "All fields are required." });
      return;
    }
    if (newPassword !== confirmPassword) {
      notify({ type: "error", title: "Error", message: "New passwords do not match." });
      return;
    }

    setSubmitting(true);
    try {
      await changePassword({
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      notify({ type: "success", title: "Password Changed", message: "Redirecting to login..." });
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      notify({ type: "error", title: "Error", message: err.message || "Failed to change password" });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="gwContentGrid">
        <section className="gwFormStack">
          <div className="gwPageHeader">
            <div>
              <div className="gwPageTitle">Account Settings</div>
              <div className="gwPageSubtitle">Loading...</div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="gwContentGrid">
      <section className="gwFormStack">
        <div className="gwPageHeader">
          <div>
            <div className="gwPageTitle">Account Settings</div>
            <div className="gwPageSubtitle">
              Manage your account and change your password.
            </div>
          </div>
        </div>

        {user && (
          <div className="gwCardSection">
            <div className="gwSectionTitle">Profile</div>
            <div className="gwCardSectionBody">
              <div style={{ display: "flex", alignItems: "center", gap: 16, paddingBottom: 4 }}>
                <div
                  className="gwAvatarUpload"
                  onClick={() => fileInputRef.current?.click()}
                  title="Click to upload photo"
                >
                  {profile.avatar ? (
                    <img src={profile.avatar} alt="Avatar" className="gwAvatarImage" />
                  ) : (
                    <span className="gwAvatarInitial">
                      {(user.username || "?")[0].toUpperCase()}
                    </span>
                  )}
                  <div className="gwAvatarOverlay">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                      <circle cx="12" cy="13" r="4" />
                    </svg>
                  </div>
                  {profile.avatar && (
                    <button
                      type="button"
                      className="gwAvatarRemove"
                      onClick={removeAvatar}
                      title="Remove photo"
                    >
                      <img src={xmarkIcon} alt="" style={{ width: 8, height: 8, filter: "brightness(0) invert(1)" }} />
                    </button>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleAvatarUpload}
                  />
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 16 }}>{user.username}</div>
                  <div style={{ fontSize: 13, color: "var(--gw-muted)", textTransform: "capitalize" }}>
                    {user.role}
                  </div>
                </div>
              </div>
              <div className="gwFieldRow">
                <div className="gwField">
                  <label className="gwLabel">First Name</label>
                  <input
                    className="gwInput"
                    placeholder="First name"
                    value={profile.first_name}
                    onChange={(e) => updateField("first_name", e.target.value)}
                  />
                </div>
                <div className="gwField">
                  <label className="gwLabel">Last Name</label>
                  <input
                    className="gwInput"
                    placeholder="Last name"
                    value={profile.last_name}
                    onChange={(e) => updateField("last_name", e.target.value)}
                  />
                </div>
              </div>
              <div className="gwFieldRow">
                <div className="gwField">
                  <label className="gwLabel">Email</label>
                  <input
                    className="gwInput"
                    type="email"
                    placeholder="user@example.com"
                    value={profile.email}
                    onChange={(e) => updateField("email", e.target.value)}
                  />
                </div>
                <div className="gwField">
                  <label className="gwLabel">Location</label>
                  <input
                    className="gwInput"
                    placeholder="e.g. New York, NY"
                    value={profile.location}
                    onChange={(e) => updateField("location", e.target.value)}
                  />
                </div>
              </div>
              <div className="gwFieldRow">
                <div className="gwField">
                  <label className="gwLabel">Phone</label>
                  <input
                    className="gwInput"
                    type="tel"
                    placeholder="+1 (555) 000-0000"
                    value={profile.phone}
                    onChange={(e) => updateField("phone", e.target.value)}
                  />
                </div>
                <div className="gwField">
                  <label className="gwLabel">Department</label>
                  <input
                    className="gwInput"
                    placeholder="e.g. Engineering"
                    value={profile.department}
                    onChange={(e) => updateField("department", e.target.value)}
                  />
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, paddingTop: 4 }}>
                <button
                  className="gwPrimaryBtn"
                  type="button"
                  onClick={saveProfile}
                  disabled={!profileDirty || profileSaving}
                >
                  {profileSaving ? "Saving..." : "Save Profile"}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="gwCardSection">
          <div className="gwSectionTitle">Change Password</div>
          <div className="gwCardSectionBody">
            <div className="gwField">
              <label className="gwLabel">Current Password</label>
              <PasswordInput
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>
            <div className="gwField">
              <label className="gwLabel">New Password</label>
              <PasswordInput
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
              />
              <PasswordRules password={newPassword} />
            </div>
            <div className="gwField">
              <label className="gwLabel">Confirm New Password</label>
              <PasswordInput
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
              />
            </div>
            <div style={{ paddingTop: 4 }}>
              <button
                className="gwPrimaryBtn"
                type="button"
                onClick={handleChangePassword}
                disabled={submitting || !currentPassword || !newPassword || !confirmPassword || !validatePassword(newPassword)}
              >
                {submitting ? "Saving..." : "Change Password"}
              </button>
            </div>
          </div>
        </div>

      </section>
    </div>
  );
};

export { AccountSettingsPage };
